import React from "react";
import clsx from "clsx";
import { PressEvent, Button as ReactAriaButton } from "react-aria-components";
import {
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Edit,
  Trash,
  Copy,
  Search,
  Plus,
  Download,
  Menu,
  ArrowLeft,
  X,
  MoreVertical,
  Save,
  Minus,
  Code,
} from "react-feather";
import { ButtonContext, useContextProps } from "react-aria-components";

import { BUTTON_ICON } from "./button-utils";
import styles from "./button.module.css";

export interface ButtonProps {
  id?: string;
  type?: "submit" | "button";
  isDisabled?: boolean;
  className?: string;
  variant?: "primary" | "secondary" | "tertiary" | "danger";
  text?: string;
  "aria-label"?: string;
  icon?: { type: ButtonIconProps["type"]; align?: "left" | "right" };
  onPress?: (event?: PressEvent) => void;
  slot?: string;
  display?: {
    scale?: "standard" | "inline";
  };
}

const ICON_DEFAULT = { align: "left" } as const;
interface ButtonIconProps {
  type?: (typeof BUTTON_ICON)[keyof typeof BUTTON_ICON];
}

function ButtonIcon({ type }: ButtonIconProps) {
  // This can be further broken down as we identify new use cases
  const size = 18;
  switch (type) {
    case BUTTON_ICON.settings: {
      return <Settings size={size} />;
    }

    case BUTTON_ICON.arrowLeft: {
      return <ArrowLeft size={size} />;
    }

    case BUTTON_ICON.chevronLeft: {
      return <ChevronLeft size={size} />;
    }

    case BUTTON_ICON.chevronRight: {
      return <ChevronRight size={size} />;
    }

    case BUTTON_ICON.chevronUp: {
      return <ChevronUp size={size} />;
    }

    case BUTTON_ICON.chevronDown: {
      return <ChevronDown size={size} />;
    }

    case BUTTON_ICON.edit: {
      return <Edit size={size} />;
    }

    case BUTTON_ICON.trash: {
      return <Trash size={size} />;
    }

    case BUTTON_ICON.copy: {
      return <Copy size={size} />;
    }

    case BUTTON_ICON.search: {
      return <Search size={size} />;
    }

    case BUTTON_ICON.plus: {
      return <Plus size={size} />;
    }

    case BUTTON_ICON.download: {
      return <Download size={size} />;
    }

    case BUTTON_ICON.menu: {
      return <Menu size={size} />;
    }

    case BUTTON_ICON.x: {
      return <X size={size} />;
    }

    case BUTTON_ICON.moreVertical: {
      return <MoreVertical size={size} />;
    }

    case BUTTON_ICON.save: {
      return <Save size={size} />;
    }

    case BUTTON_ICON.minus: {
      return <Minus size={size} />;
    }

    case BUTTON_ICON.code: {
      return <Code size={size} />;
    }

    default: {
      return null;
    }
  }
}

export default React.forwardRef(
  (props: ButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    // Merge the local props and ref with the ones provided via context.
    [props, ref] = useContextProps(props, ref, ButtonContext);

    let iconWithDefaults = props.icon;
    let content = [
      iconWithDefaults && (
        <ButtonIcon key="icon" type={iconWithDefaults.type} />
      ),
      props.text,
    ];

    if (props.icon) {
      iconWithDefaults = { ...ICON_DEFAULT, ...props.icon };
    }

    if (iconWithDefaults?.align === "right") {
      content = content.reverse();
    }

    return (
      <ReactAriaButton
        {...props}
        className={clsx(
          styles.button,
          styles[props.variant || "primary"],
          props.className,
          {
            [styles.iconOnly]: !props.text,
            [styles.iconLeft]: iconWithDefaults?.align === "left" && props.text,
            [styles.iconRight]:
              iconWithDefaults?.align === "right" && props.text,
            [styles.inline]: props.display?.scale === "inline",
          },
        )}
        ref={ref}
      >
        {content}
      </ReactAriaButton>
    );
  },
);
